import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, BarChart3 } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import { getAuth } from '../utils/auth';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = getAuth();
  const [servers, setServers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    ip_address: '',
    port: '25565',
    description: '',
    version: '',
    category: '',
    website: '',
    discord: ''
  });

  useEffect(() => {
    if (!user || (user.role !== 'server_owner' && user.role !== 'admin')) {
      toast.error('Access denied');
      navigate('/');
      return;
    }
    fetchServers();
    fetchCategories();
  }, []);

  const fetchServers = async () => {
    try {
      const response = await api.get('/owner/servers');
      setServers(response.data.servers);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch servers');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/servers', formData);
      toast.success('Server added successfully! Pending approval.');
      setShowAddDialog(false);
      setFormData({
        name: '',
        ip_address: '',
        port: '25565',
        description: '',
        version: '',
        category: '',
        website: '',
        discord: ''
      });
      fetchServers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add server');
    }
  };

  const handleEdit = (server) => {
    setEditingServer(server);
    setFormData({
      name: server.name,
      ip_address: server.ip_address,
      port: server.port.toString(),
      description: server.description,
      version: server.version,
      category: server.category,
      website: server.website || '',
      discord: server.discord || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await api.put(`/servers/${editingServer.id}`, formData);
      toast.success('Server updated successfully');
      setShowEditDialog(false);
      setEditingServer(null);
      setFormData({
        name: '',
        ip_address: '',
        port: '25565',
        description: '',
        version: '',
        category: '',
        website: '',
        discord: ''
      });
      fetchServers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update server');
    }
  };

  const handleDelete = async (serverId) => {
    if (!window.confirm('Are you sure you want to delete this server?')) return;
    
    try {
      await api.delete(`/servers/${serverId}`);
      toast.success('Server deleted successfully');
      fetchServers();
    } catch (error) {
      toast.error('Failed to delete server');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-500/10 text-green-400 border-green-500/20',
      pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      rejected: 'bg-red-500/10 text-red-400 border-red-500/20'
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold uppercase border rounded-sm ${styles[status]}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen noise-bg">
      <Navbar />
      
      <div className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter uppercase" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid="dashboard-title">
                MY <span className="text-[#00E055]">SERVERS</span>
              </h1>
              <p className="text-muted-foreground mt-2">Manage your server listings</p>
            </div>
            
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold glow-primary" data-testid="add-server-button">
                  <Plus className="w-5 h-5 mr-2" />
                  ADD SERVER
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-border">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold" style={{fontFamily: 'Unbounded, sans-serif'}}>ADD NEW SERVER</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Server Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-muted border-border"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ip_address">IP Address *</Label>
                      <Input
                        id="ip_address"
                        value={formData.ip_address}
                        onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                        required
                        className="bg-muted border-border"
                        data-testid="input-ip"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="port">Port *</Label>
                      <Input
                        id="port"
                        type="number"
                        value={formData.port}
                        onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                        required
                        className="bg-muted border-border"
                        data-testid="input-port"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      className="bg-muted border-border"
                      data-testid="input-description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="version">Minecraft Version *</Label>
                      <Input
                        id="version"
                        value={formData.version}
                        onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                        required
                        placeholder="1.20.4"
                        className="bg-muted border-border"
                        data-testid="input-version"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                        <SelectTrigger className="bg-muted border-border" data-testid="select-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website URL</Label>
                    <Input
                      id="website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://..."
                      className="bg-muted border-border"
                      data-testid="input-website"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discord">Discord Invite</Label>
                    <Input
                      id="discord"
                      value={formData.discord}
                      onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
                      placeholder="https://discord.gg/..."
                      className="bg-muted border-border"
                      data-testid="input-discord"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold" 
                    data-testid="submit-server-button"
                  >
                    ADD SERVER
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Edit Server Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-border">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold" style={{fontFamily: 'Unbounded, sans-serif'}}>EDIT SERVER</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdate} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Server Name *</Label>
                    <Input
                      id="edit-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-muted border-border"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-ip">IP Address *</Label>
                      <Input
                        id="edit-ip"
                        value={formData.ip_address}
                        onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                        required
                        className="bg-muted border-border"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-port">Port *</Label>
                      <Input
                        id="edit-port"
                        type="number"
                        value={formData.port}
                        onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                        required
                        className="bg-muted border-border"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-description">Description *</Label>
                    <Textarea
                      id="edit-description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      className="bg-muted border-border"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-version">Minecraft Version *</Label>
                      <Input
                        id="edit-version"
                        value={formData.version}
                        onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                        required
                        placeholder="1.20.4"
                        className="bg-muted border-border"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-category">Category *</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                        <SelectTrigger className="bg-muted border-border">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-website">Website URL</Label>
                    <Input
                      id="edit-website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://..."
                      className="bg-muted border-border"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-discord">Discord Invite</Label>
                    <Input
                      id="edit-discord"
                      value={formData.discord}
                      onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
                      placeholder="https://discord.gg/..."
                      className="bg-muted border-border"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold"
                  >
                    UPDATE SERVER
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Servers List */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-[#00E055] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : servers.length > 0 ? (
            <div className="space-y-4" data-testid="servers-list">
              {servers.map((server) => (
                <div key={server.id} className="bg-[#0A0A0A] border border-border rounded-lg p-6" data-testid={`server-item-${server.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid={`server-name-${server.id}`}>
                          {server.name}
                        </h3>
                        {getStatusBadge(server.status)}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{server.description}</p>
                      <div className="flex flex-wrap gap-2">
                        <span 
                          className="px-3 py-1 text-[10px] font-black tracking-widest uppercase bg-[#00E055]/10 text-[#00E055] border border-[#00E055]/20 rounded-sm flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,224,85,0.05)]"
                          style={{ fontFamily: 'Unbounded, sans-serif' }}
                        >
                          <span className="w-1 h-1 rounded-full bg-[#00E055] shadow-[0_0_5px_#00E055]" />
                          {server.category}
                        </span>
                        <span className="px-3 py-1 text-[10px] font-bold tracking-widest uppercase bg-white/5 text-white/40 border border-white/10 rounded-sm">
                          {server.ip_address}:{server.port}
                        </span>
                        <span className="px-3 py-1 text-[10px] font-bold tracking-widest uppercase bg-white/5 text-white/40 border border-white/10 rounded-sm" data-testid={`server-votes-${server.id}`}>
                          {server.vote_count} votes
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEdit(server)}
                        variant="outline"
                        size="sm"
                        data-testid={`edit-button-${server.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        EDIT
                      </Button>
                      <Button
                        onClick={() => navigate(`/servers/${server.slug || server.id}`)}
                        variant="outline"
                        size="sm"
                        data-testid={`view-button-${server.id}`}
                      >
                        VIEW
                      </Button>
                      <Button
                        onClick={() => handleDelete(server.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-button-${server.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-[#0A0A0A] border border-border rounded-lg">
              <p className="text-muted-foreground text-lg mb-4">You haven't added any servers yet.</p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold">
                <Plus className="w-5 h-5 mr-2" />
                ADD YOUR FIRST SERVER
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
